import { createSelector, createSlice, Dispatch } from '@reduxjs/toolkit';
import axios from 'axios';
import { serializeError } from 'serialize-error';
import { CONFIG_FILE_PATH } from '../api/api.constants';
import { ErrorConverter } from '../errorHandling/ErrorConverter';
import { getApiKeyFromResponse, getEnvironmentUrls } from '../api/api.helpers';
import { setApiKey } from '../api/api.state';
import { getApplicationBaseUrl } from '../application/application.helpers';
import { CapturedApiError } from '../errorHandling/errorHandling.types';
import { ERROR_LOG_API_ENDPOINT } from '../errorHandling/errorHandling.config';
import { ErrorLogger } from '../errorHandling/ErrorLogger';
import {
  AppSettingsState,
  CoreState,
  Environment,
  HttpErrorResponse,
  HttpResponse,
  InvAction,
  SystemId
} from '../interfaces';
import {
  isLocalDevelopmentEnvironment,
  isServerMimicMode,
  getServerMimicBasePath
} from './app.environment';
import { EMPTY_ENVIRONMENT } from './app.environment.constants';
import {
  appSettingsNamespace,
  LOCAL_ENVIRONMENT_VALUE,
  setSelectedEnvironmentType,
  setSelectedSystemIdType
} from './app.settings.constants';
import {
  getEnvironmentFromCurrentHost,
  getLoginUrl as getLoginUrlSetting
} from './app.settings.helpers';
import {
  ApplicationConfigFile,
  LoadApplicationConfigFile
} from './app.settings.interfaces';

const EMPTY_OBJECT = {} as ApplicationConfigFile;

// Reducers
const INITIAL_STATE: AppSettingsState = {
  selectedEnvironment: EMPTY_ENVIRONMENT,
  selectedSystemId: '',
  isLoading: true,
  isSessionStorageRead: false,
  data: EMPTY_OBJECT,
  loadError: undefined
};

const slice = createSlice({
  name: appSettingsNamespace,
  initialState: INITIAL_STATE,
  reducers: {
    [setSelectedEnvironmentType]: (state, action: InvAction<Environment>) => {
      state.selectedEnvironment = action.payload;
    },
    [setSelectedSystemIdType]: (state, action: InvAction<SystemId>) => {
      state.selectedSystemId = action.payload;
    },
    setIsLoading(state, action: InvAction<boolean>) {
      state.isLoading = action.payload;
    },
    setConfiguration(state, action: InvAction<ApplicationConfigFile>) {
      state.data = action.payload || EMPTY_OBJECT;
    },
    setLoadError(state, action: InvAction<CapturedApiError>) {
      state.loadError = action.payload;
    },
    setIsSessionStorageRead(state, action: InvAction<boolean>) {
      state.isSessionStorageRead = action.payload;
    },
    setHostedContentUrl(state, action: InvAction<string>) {
      state.data.hostedContentUrl = action.payload;
    }
  }
});

export default slice.reducer;

// Actions
export const {
  setConfiguration,
  setHostedContentUrl,
  setIsLoading,
  setIsSessionStorageRead,
  setLoadError,
  setSelectedEnvironment,
  setSelectedSystemId
} = slice.actions;

/** Load the config.json file and store in state management. */
export function loadConfigFile({
  environment,
  applicationBasePath
}: LoadApplicationConfigFile) {
  return (dispatch: Dispatch) => {
    const environmentUrls = getEnvironmentUrls(environment);
    let url = CONFIG_FILE_PATH;

    dispatch(setIsLoading(true));

    if (environment.value) {
      if (environment.value === LOCAL_ENVIRONMENT_VALUE) {
        if (isLocalDevelopmentEnvironment()) {
          //'local' environment on localhost
          if (isServerMimicMode()) {
            // Server mimic mode: use the webpack-defined base path
            const serverMimicBasePath = getServerMimicBasePath();
            url = serverMimicBasePath
              ? `${serverMimicBasePath}${CONFIG_FILE_PATH}`
              : CONFIG_FILE_PATH;
          } else {
            // Regular local development: include applicationBasePath for nginx setups with multiple apps
            url = applicationBasePath
              ? `${applicationBasePath}${CONFIG_FILE_PATH}`
              : CONFIG_FILE_PATH;
          }
        } else {
          //'local' environment on edge
          url = `${getApplicationBaseUrl(
            getEnvironmentFromCurrentHost(),
            applicationBasePath
          )}${CONFIG_FILE_PATH}`;
        }
      } else {
        //environment other than 'local' selected
        url = `${getApplicationBaseUrl(
          environment.value,
          applicationBasePath
        )}${CONFIG_FILE_PATH}`;
      }
    }

    axios
      .get(url)
      // @ts-ignore: Type 'AxiosRequestConfig' is not assignable to type '{ method: Method; url: string; }'
      .then((response: HttpResponse) => {
        const apiKey = getApiKeyFromResponse(response);

        if (apiKey || isLocalDevelopmentEnvironment()) {
          dispatch(setApiKey(apiKey));
          dispatch(setConfiguration(response.data));
          dispatch(setHostedContentUrl(environmentUrls.hostedContentUrl));

          return Promise.resolve(undefined);
        }

        return Promise.reject(new Error('Unable to set API key.'));
      })
      .catch((error: HttpErrorResponse) => {
        const serializedError = serializeError(error);
        const capturedError = ErrorConverter.fromHttpError(
          serializedError as unknown as CapturedApiError
        );

        const logger = new ErrorLogger();
        logger
          .submit({
            capturedError,
            apiFunc: (payload) =>
              axios.post(
                `${environmentUrls.services}/${ERROR_LOG_API_ENDPOINT}`,
                payload
              ),
            applicationContext: {
              basePath: applicationBasePath,
              glyph: '',
              isActive: true,
              isDeployed: true,
              isStandalone: true,
              name: '<Unknown>',
              order: 0,
              preferenceKey: '<unknown>',
              securityAttributeId: 0
            },
            userContext: {
              displayLanguage: '<unknown>',
              language: '<unknown>'
            }
          })
          .then(() => {
            dispatch(setLoadError(capturedError));
          });
      })
      .finally(() => {
        dispatch(setIsLoading(false));
      });
  };
}

// Selectors
const getAppSettings = (state: CoreState) => state.core.appSettings;

export const getIsEnvironmentSet = createSelector(
  [getAppSettings],
  (settings) => settings.selectedEnvironment !== EMPTY_ENVIRONMENT
);

export const getLoadError = createSelector(
  [getAppSettings],
  (settings) => settings.loadError
);

export const getSelectedEnvironment = createSelector(
  [getAppSettings],
  (settings) => settings.selectedEnvironment
);

export const getSelectedSystemId = createSelector(
  [getAppSettings],
  (settings) => settings.selectedSystemId
);

export const getIsLoading = createSelector(
  [getAppSettings],
  (settings) => settings.isLoading
);

export const getIsSessionStorageRead = createSelector(
  [getAppSettings],
  (settings) => settings.isSessionStorageRead
);

export const getConfiguration = createSelector(
  [getAppSettings],
  (settings) => settings.data
);

export const getLoginUrl = createSelector([getConfiguration], (data) =>
  getLoginUrlSetting(data)
);

export const getHostedContentUrl = createSelector(
  [getConfiguration],
  (data) => data.hostedContentUrl
);
